/**
 * Stats Component
 * Отображение статистики для админов
 */

import React, { useState, useEffect } from "react";
import { Section, ToolbarButton } from "./UI";
import { statsService } from "../services/statsService";

export function Stats({ setView }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const statsData = await statsService.getOverview();
      setStats(statsData);
    } catch (err) {
      console.error("Failed to load stats:", err);
      setError(err.message || "Ошибка загрузки статистики");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Section title="📊 Статистика" right={<ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Загрузка статистики...</p>
          </div>
        </div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section title="📊 Статистика" right={<ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>}>
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">❌ {error}</p>
          <ToolbarButton onClick={loadStats}>Попробовать снова</ToolbarButton>
        </div>
      </Section>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Section 
      title="📊 Статистика" 
      right={
        <div className="flex gap-2">
          <ToolbarButton onClick={loadStats}>🔄 Обновить</ToolbarButton>
          <ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Общая статистика */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 rounded-2xl p-6 text-white">
          <div className="text-4xl font-bold mb-2">{stats.total_leads}</div>
          <div className="text-lg opacity-90">Всего лидов</div>
        </div>

        {/* По событиям */}
        <div>
          <h3 className="text-lg font-semibold mb-3">По событиям</h3>
          <div className="space-y-2">
            {Object.entries(stats.by_event || {}).map(([eventCode, eventData]) => (
              <div 
                key={eventCode} 
                className="flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div>
                  <div className="font-medium">{eventData.name || eventCode}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{eventCode}</div>
                </div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {eventData.count}
                </div>
              </div>
            ))}
          </div>
          {Object.keys(stats.by_event || {}).length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">Нет данных</p>
          )}
        </div>

        {/* По менеджерам */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Топ менеджеров</h3>
          <div className="space-y-2">
            {(stats.by_manager || []).slice(0, 10).map((manager, index) => {
              const medals = ["🥇", "🥈", "🥉"];
              const medal = medals[index] || "👤";
              
              return (
                <div 
                  key={manager.telegram_id} 
                  className="flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{medal}</span>
                    <div>
                      <div className="font-medium">
                        {manager.first_name} {manager.last_name ? manager.last_name.charAt(0) + '.' : ''}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {manager.username ? `@${manager.username}` : `ID: ${manager.telegram_id}`}
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">
                    {manager.count}
                  </div>
                </div>
              );
            })}
          </div>
          {(stats.by_manager || []).length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">Нет данных</p>
          )}
        </div>

        {/* По типам лидов */}
        {stats.by_lead_type && Object.keys(stats.by_lead_type).length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">По типам лидов</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(stats.by_lead_type).map(([type, count]) => (
                <div 
                  key={type}
                  className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center"
                >
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                    {count}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{type}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* По источникам трафика */}
        {stats.by_traffic_source && Object.keys(stats.by_traffic_source).length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">По источникам трафика</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(stats.by_traffic_source).map(([source, count]) => (
                <div 
                  key={source}
                  className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center"
                >
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-1">
                    {count}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{source}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Статус работы */}
        {stats.by_works_with_us && Object.keys(stats.by_works_with_us).length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3">Статус работы</h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(stats.by_works_with_us).map(([status, count]) => {
                const statusLabels = {
                  yes: { label: "Уже с нами", color: "text-green-600 dark:text-green-400" },
                  no: { label: "Пока нет", color: "text-gray-600 dark:text-gray-400" },
                  unknown: { label: "Не уверен", color: "text-yellow-600 dark:text-yellow-400" }
                };
                const statusInfo = statusLabels[status] || { label: status, color: "" };
                
                return (
                  <div 
                    key={status}
                    className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center"
                  >
                    <div className={`text-2xl font-bold mb-1 ${statusInfo.color}`}>
                      {count}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{statusInfo.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {stats.total_leads > 0 ? Math.round((count / stats.total_leads) * 100) : 0}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}
